import { httpClient } from './http-client';

/**
 * Interface representing a categorization rule.
 */
export interface CategorizationRule {
  id: string;
  household_id: string;
  name: string;
  match_field: string;
  match_type: string;
  match_value: string;
  match_value_2?: string;
  category_id: string;
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Request payload for creating a categorization rule.
 */
export interface CreateCategorizationRuleRequest {
  name: string;
  match_field: string;
  match_type: string;
  match_value: string;
  match_value_2?: string;
  category_id: string;
  priority: number;
  is_active?: boolean;
}

/**
 * Request payload for updating a categorization rule.
 */
interface UpdateCategorizationRuleRequest {
  name?: string;
  match_field?: string;
  match_type?: string;
  match_value?: string;
  match_value_2?: string;
  category_id?: string;
  priority?: number;
  is_active?: boolean;
}

/**
 * Result from testing a categorization rule against transactions.
 */
export interface RuleTestResult {
  transaction_id: string;
  description?: string;
  amount: string;
  rule_id: string;
  rule_name: string;
  category_id: string;
  category_name: string;
}

/**
 * Service for managing auto-categorization rules.
 *
 * Communicates with the /categorization-rules backend API endpoints.
 */
export class CategorizationRulesService {
  private static readonly BASE_URL = '/categorization-rules';

  /**
   * List all categorization rules for the current household.
   *
   * Returns:
   *     Object containing categorization_rules array and total count.
   */
  static async list(): Promise<{ categorization_rules: CategorizationRule[]; total: number }> {
    const raw = await httpClient.get<any>(CategorizationRulesService.BASE_URL);
    const data = (raw as any).data || raw;
    return {
      categorization_rules: data.categorization_rules || [],
      total: data.total || 0,
    };
  }

  /**
   * Create a new categorization rule.
   *
   * Args:
   *     data (CreateCategorizationRuleRequest): The rule data.
   *
   * Returns:
   *     CategorizationRule: The created rule.
   */
  static async create(data: CreateCategorizationRuleRequest): Promise<CategorizationRule> {
    const raw = await httpClient.post<any>(CategorizationRulesService.BASE_URL, data);
    return (raw as any).data || raw;
  }

  /**
   * Update an existing categorization rule.
   *
   * Args:
   *     id (string): The rule ID.
   *     data (UpdateCategorizationRuleRequest): The fields to update.
   *
   * Returns:
   *     CategorizationRule: The updated rule.
   */
  static async update(id: string, data: UpdateCategorizationRuleRequest): Promise<CategorizationRule> {
    const raw = await httpClient.put<any>(
      `${CategorizationRulesService.BASE_URL}/${id}`,
      data
    );
    return (raw as any).data || raw;
  }

  /**
   * Delete a categorization rule.
   *
   * Args:
   *     id (string): The rule ID.
   */
  static async delete(id: string): Promise<void> {
    await httpClient.delete(`${CategorizationRulesService.BASE_URL}/${id}`);
  }

  /**
   * Apply all active rules to uncategorized transactions.
   *
   * Returns:
   *     Object containing the number of transactions updated.
   */
  static async applyRules(): Promise<{ transactions_updated: number }> {
    const raw = await httpClient.post<any>(`${CategorizationRulesService.BASE_URL}/apply`);
    const data = (raw as any).data || raw;
    return { transactions_updated: data.transactions_updated || 0 };
  }

  /**
   * Test all active rules against transactions (dry run).
   *
   * Returns:
   *     Object containing test_results array and total count.
   */
  static async testRules(): Promise<{ test_results: RuleTestResult[]; total: number }> {
    const raw = await httpClient.post<any>(`${CategorizationRulesService.BASE_URL}/test`);
    const data = (raw as any).data || raw;
    return {
      test_results: data.test_results || [],
      total: data.total || 0,
    };
  }
}
